import { Head, Link, usePage } from "@inertiajs/react";
import { Button } from "@/components/ui/button.tsx";
import { ArrowLeft, Building2 } from "lucide-react";
import {CommunityDetailType, CommunityStats} from "@/pages/communities/types.ts";


type CommunityShowProps = {
  community: CommunityDetailType;
  stats: CommunityStats;
  can_edit: boolean;
};

export default function CommunityShow() {
  const { community, stats, can_edit } = usePage<CommunityShowProps>().props;

  return (
    <>
      <Head title={`Communaute ${community.name}`} />
      <div className="space-y-6 p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link href="/communities">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour
              </Button>
            </Link>
            {community.logo_url ? (
              <img
                src={community.logo_url}
                alt={`Logo ${community.name}`}
                className="h-16 w-16 rounded-md border border-border object-contain"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-md border border-border bg-secondary">
                <Building2 className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-foreground">{community.name}</h1>
              <p className="text-sm text-muted-foreground">
                Code {community.code} {community.status ? `• ${community.status}` : ""}
              </p>
            </div>
          </div>

          {can_edit && (
            <Link href={`/communities/${community.id}/edit`}>
              <Button>Modifier</Button>
            </Link>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Utilisateurs</p>
            <p className="mt-2 text-3xl font-semibold text-foreground">{stats.users}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Contribuables</p>
            <p className="mt-2 text-3xl font-semibold text-foreground">{stats.tax_payers}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Appareils</p>
            <p className="mt-2 text-3xl font-semibold text-foreground">{stats.devices.total}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Sessions</p>
            <p className="mt-2 text-3xl font-semibold text-foreground">{stats.collect_sessions.total}</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="text-lg font-semibold text-foreground">Appareils</h2>
            <div className="mt-4 space-y-3 text-sm text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>Total</span>
                <span className="font-semibold text-foreground">{stats.devices.total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Assignes</span>
                <span className="font-semibold text-foreground">{stats.devices.assigned}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Bloques</span>
                <span className="font-semibold text-foreground">{stats.devices.blocked}</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="text-lg font-semibold text-foreground">Sessions de collecte</h2>
            <div className="mt-4 space-y-3 text-sm text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>Ouvertes</span>
                <span className="font-semibold text-foreground">{stats.collect_sessions.open}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Fermees</span>
                <span className="font-semibold text-foreground">{stats.collect_sessions.closed}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Reconciliees</span>
                <span className="font-semibold text-foreground">{stats.collect_sessions.reconciled}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground">Paiements</h2>
          <div className="mt-4 grid gap-3 text-sm text-muted-foreground md:grid-cols-3">
            <div className="rounded-md border border-border/60 bg-secondary/60 px-3 py-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total paiements</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{stats.payments.total}</p>
            </div>
            <div className="rounded-md border border-border/60 bg-secondary/60 px-3 py-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Collecte 30 jours</p>
              <p className="mt-2 text-lg font-semibold text-foreground">
                {stats.payments.total_collected_last_30_days.toLocaleString()}
              </p>
            </div>
            <div className="rounded-md border border-border/60 bg-secondary/60 px-3 py-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Collecte totale</p>
              <p className="mt-2 text-lg font-semibold text-foreground">
                {stats.payments.total_collected_all_time.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground">Coordonnees</h2>
          <div className="mt-4 grid gap-3 text-sm text-muted-foreground md:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Adresse</p>
              <p className="mt-1 text-foreground">{community.address || "-"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Localisation</p>
              <p className="mt-1 text-foreground">{community.location || "-"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Telephone</p>
              <p className="mt-1 text-foreground">{community.phone || "-"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Email</p>
              <p className="mt-1 text-foreground">{community.email || "-"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Latitude</p>
              <p className="mt-1 text-foreground">
                {community.lat !== undefined && community.lat !== null ? community.lat : "-"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Longitude</p>
              <p className="mt-1 text-foreground">
                {community.lng !== undefined && community.lng !== null ? community.lng : "-"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
